import { getSpec } from "@specs"
import * as Sentry from "@sentry/react"

import { HOST } from "@components/constants"

import RequestFailedError from "./errors/RequestFailedError"
import UnexpectedReponseError from "./errors/UnexpectedReponseError"

const MAX_PER_PAGE_LIMIT = 300


const getUrlQueryString = query => {
  return Object.keys(query)
    .map(key => `${key}=${encodeURIComponent(query[key])}`)
    .join('&')
}


const getMethod = (operationUrl) => {
  const [ , , , , service, operationId ] = operationUrl.split('/')

  const spec = getSpec(service)

  const operationSpec = spec.paths[`/${operationId}`]

  if (!operationSpec) {
    throw new Error(`Operation "${operationId}" not defined for "${service}" service.`)
  }

  const [ method ] = Object.keys(operationSpec)

  return method.toUpperCase()
}


const getOperationId = operationUrl => {
  const [ , operationId ] = operationUrl.split('/api/')
  return operationId
}


const request = async (operationUrl, parameters = {}, headers = {}) => {
  const method = getMethod(operationUrl)
  const operationId = getOperationId(operationUrl)

  const { mutation, ...query } = parameters

  const options = {
    headers: { ...headers, 'Content-Type': 'application/json' },
    method,
  }

  if (mutation) {
    options.body = JSON.stringify(mutation)
  }

  const hasQuery = Object.keys(query).length > 0

  if (hasQuery) {
    operationUrl += `?${getUrlQueryString(query)}`
  }

  const setSentryExtra = error =>
    Sentry.setExtras({
      errorJson: JSON.stringify(error, null, 2),
      mutationJson: options.body,
      method,
      operationUrl,
    })

  let response

  try {
    response = await fetch(operationUrl, options)

  } catch (error) {
    setSentryExtra(error)
    throw new RequestFailedError(operationId)

  }

  let data
  let error
  let pageInfo

  const hasBody = response.status !== 204

  if (hasBody) {
    const result = await response.json()

    data     = result.data
    error    = result.error
    pageInfo = result.pageInfo
  }

  if (!response.ok) {
    setSentryExtra(error || response)
    const err = new UnexpectedReponseError(operationId)

    if (error) {
      err.code = error.code
      err.originalError = error
    }

    throw err
  }

  return { data, pageInfo, headers: response.headers }
}

const apiRequest = async (operation, parameters = {}, headers = {}) => {
  const isIndexOperation = operation.includes('/Index')
  const operationUrl = `${HOST}/api/${operation}`

  if (!isIndexOperation) {
    const result = await request(operationUrl, parameters, headers)

    return result
  }

  const { limit: customLimit } = parameters

  let data = []
  let isLastPage = false
  let exclusiveStartKey = null

  do {
    const newParams = {
      limit: MAX_PER_PAGE_LIMIT,
      ...parameters,
    }

    if (exclusiveStartKey) {
      newParams.exclusiveStartKey = exclusiveStartKey
    }

    const response = await request(operationUrl, newParams, headers)
    const { data: responseData, pageInfo: responsePageInfo } = response

    data = [...data, ...responseData]

    const { lastEvaluatedKey: nextExclusiveStartKey } = responsePageInfo

    exclusiveStartKey = nextExclusiveStartKey
    isLastPage = !exclusiveStartKey

    if (customLimit) {
      isLastPage = true
    }
  } while (!isLastPage)

  return { data }
}

export {
  apiRequest
}

export default request
